import styles from "./styles.module.scss";
import cx from 'classnames';
import { forwardRef, useState, useRef, useEffect } from 'react';
import Tooltip from 'src/components/tooltip';

interface PhoneVerificationInputProps {
  label: string;
  value?: string;
  name?: string;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errorMessage?: string;
  disabled?: boolean;
  className?: string;
  countryCode: {
    value: string;
    options: { code: string; label: string }[];
    onChange: (code: string) => void;
  };
  actionButton?: {
    text: string;
    onClick: () => void;
    icon?: string;
    disabled?: boolean;
  };
  verifiedBadge?: {
    show: boolean;
    text: string;
  };
  infoIcon?: {
    show: boolean;
    tooltip?: string;
  };
}

const PhoneVerificationInput = forwardRef<HTMLInputElement, PhoneVerificationInputProps>(({
  label,
  value,
  name,
  placeholder,
  onChange,
  errorMessage,
  disabled,
  className,
  countryCode,
  actionButton,
  verifiedBadge,
  infoIcon,
}, ref) => {

  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCountryDropdown(false);
      }
    };

    if (showCountryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCountryDropdown]);

  const handleCountrySelect = (code: string) => {
    countryCode.onChange(code);
    setShowCountryDropdown(false);
  };

  return (
    <div className={cx(styles.container, className)}>
      <div className={styles.labelContainer}>
        <label className={styles.label}>{label}</label>
        {infoIcon?.show && (
          <Tooltip placement="left" content={infoIcon.tooltip}>
            <i className={cx('paperflite-info', styles.infoIcon)} />
          </Tooltip>
        )}
      </div>
      <div className={styles.inputWrapper} ref={dropdownRef}>
        <div
          className={styles.countryCodeSelector}
          onClick={(e) => {
            e.stopPropagation();
            setShowCountryDropdown(!showCountryDropdown);
          }}
        >
          <span className={styles.countryCodeText}>{countryCode.value}</span>
          <i className={cx('paperflite-up-arrow', styles.dropdownIcon)} />
        </div>
        {showCountryDropdown && (
          <div className={styles.countryDropdownMenu}>
            {countryCode.options.map((option) => (
              <div
                key={option.code}
                className={styles.countryOption}
                onClick={() => handleCountrySelect(option.code)}
              >
                <div className={cx(styles.radioButton, {
                  [styles.selected]: countryCode.value === option.code
                })}>
                  {countryCode.value === option.code && (
                    <i className="paperflite-tick" />
                  )}
                </div>
                <span className={styles.countryLabel}>
                  {option.code} {option.label}
                </span>
              </div>
            ))}
          </div>
        )}
        <input
          ref={ref}
          className={cx(styles.field, {
            [styles.errorField]: errorMessage,
            [styles.fieldWithAction]: actionButton || verifiedBadge,
            [styles.fieldWithCountryCode]: true
          })}
          type="text"
          value={value}
          name={name}
          placeholder={placeholder ?? 'Enter your mobile number'}
          onChange={onChange}
          disabled={disabled}
        />
        {actionButton && (
          <button
            type="button"
            className={cx(styles.actionButton, {
              [styles.disabled]: actionButton.disabled
            })}
            onClick={actionButton.onClick}
            disabled={actionButton.disabled}
          >
            {actionButton.icon && <i className={actionButton.icon} />}
            {actionButton.text}
          </button>
        )}
        {verifiedBadge?.show && (
          <span className={styles.verifiedBadge}>
            <i className="paperflite-tick" /> {verifiedBadge.text}
          </span>
        )}
      </div>
      {errorMessage && <p className={styles.error}>{errorMessage}</p>}
    </div>
  );
});

PhoneVerificationInput.displayName = 'PhoneVerificationInput';

export default PhoneVerificationInput;